/**
 * Telling focus the user moved apart from focus the page moved for them.
 *
 * A tooltip should open on focus only when the user moved focus with the
 * keyboard, such as pressing Tab onto a button. It should not open when the
 * page put focus somewhere on its own. The common cases of that are:
 *
 *  - A menu, popover or dialog closes and hands focus back to the button that
 *    opened it. This happens even when the menu was closed with the keyboard,
 *    for example by pressing Enter in the page-size field. Focus then counts as
 *    keyboard focus to the browser, so `:focus-visible` alone cannot tell.
 *  - The browser window regains focus and re-focuses the last focused element.
 *
 * In both cases the focus event either has no `relatedTarget` (the element that
 * had focus before is gone, or focus came from outside the page), or the
 * previous element sits inside the menu or dialog that just closed.
 */

/** Containers that hand focus back to their opener when they close. */
const FOCUS_RETURNING_CONTAINERS = '[role="menu"], [role="dialog"], [role="alertdialog"], [role="listbox"]';

/** True when the element has focus and the browser would draw a focus ring on it. */
export function hasKeyboardFocus(element: Element | null): boolean {
  if (!element || typeof element.matches !== 'function') {
    return false;
  }
  try {
    return element.matches(':focus-visible');
  } catch {
    // A browser that does not know the selector. Treat focus as keyboard
    // focus, which is how focus behaved before the check existed.
    return true;
  }
}

/**
 * True when this focus event is the user moving focus with the keyboard, and
 * not the page restoring focus after a menu, a dialog or the window closed.
 */
export function isKeyboardNavigationFocus(event: FocusEvent): boolean {
  const focusedElement = event.target instanceof Element ? event.target : null;
  if (!hasKeyboardFocus(focusedElement)) {
    return false;
  }
  const previousElement = event.relatedTarget;
  if (!(previousElement instanceof Element)) {
    return false;
  }
  const previousContainer = previousElement.closest(FOCUS_RETURNING_CONTAINERS);
  if (previousContainer && focusedElement && !previousContainer.contains(focusedElement)) {
    return false;
  }
  return true;
}
